import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="select"
// Applied directly to a <select> element to transform it into a searchable select
export default class extends Controller {
  static values = {
    includeBlank: Boolean,
    optionTemplateId: String,
    optgroupTemplateId: String
  }

  connect() {
    // The controller is applied to the select element itself
    if (this.element.tagName !== 'SELECT') {
      console.error('Select controller must be applied to a <select> element')
      return
    }

    this.selectedIndex = -1
    this.optionTemplateElement = this.hasOptionTemplateIdValue
      ? document.getElementById(this.optionTemplateIdValue)
      : null
    this.optgroupTemplateElement = this.hasOptgroupTemplateIdValue
      ? document.getElementById(this.optgroupTemplateIdValue)
      : null
    this.buildCustomUI()

    // Watch for option changes (e.g. from Turbo Stream updates) to re-render the custom dropdown
    this.optionsMutationObserver = new MutationObserver(() => this.refreshOptions())
    this.optionsMutationObserver.observe(this.element, { childList: true, subtree: true })
  }

  disconnect() {
    document.removeEventListener('click', this.boundHandleClickOutside)

    if (this.optionsMutationObserver) {
      this.optionsMutationObserver.disconnect()
    }

    // Restore native select
    if (this.customWrapper) {
      this.element.classList.remove('hidden')
      this.customWrapper.remove()
    }
  }

  refreshOptions() {
    if (!this.optionsListElement) return

    this.optionsListElement.innerHTML = ''
    this.populateOptions(this.optionsListElement)

    const selectedOption = this.element.selectedOptions[0]
    const hasValue = selectedOption && selectedOption.value !== ''
    const display = this.buttonElement.querySelector('[data-display]')

    if (display) {
      if (hasValue) {
        display.textContent = selectedOption.text
        display.classList.remove('text-base-content/60')
      } else {
        const blankOption = this.element.querySelector('option[value=""]')
        display.textContent = blankOption?.text || 'Selecione...'
        display.classList.add('text-base-content/60')
      }
    }
  }

  buildCustomUI() {
    // Create wrapper
    const wrapper = document.createElement('div')
    wrapper.className = 'relative w-full'
    wrapper.setAttribute('data-select', `#${this.element.id}`)
    this.customWrapper = wrapper

    // Create button
    const button = this.createButton()
    wrapper.appendChild(button)

    // Create menu
    const menu = this.createMenu()
    wrapper.appendChild(menu)

    // Insert wrapper after select and hide select
    this.element.insertAdjacentElement('afterend', wrapper)
    this.element.classList.add('hidden')

    // Store references
    this.buttonElement = button
    this.menuElement = menu
    this.searchElement = menu.querySelector('[data-search]')
    this.optionsListElement = menu.querySelector('[data-options-list]')
  }

  createButton() {
    const selectedOption = this.element.selectedOptions[0]
    const displayText = selectedOption?.text || this.element.querySelector('option[value=""]')?.text || 'Selecione...'
    const hasValue = selectedOption && selectedOption.value !== ''

    const button = document.createElement('button')
    button.type = 'button'
    button.className = this.element.className

    button.onclick = (e) => { e.preventDefault(); this.toggle() }

    button.innerHTML = `
      <span data-display class="${hasValue ? '' : 'text-base-content/60'} truncate">${displayText}</span>
    `

    return button
  }

  createMenu() {
    const menu = document.createElement('div')
    menu.className = 'hidden absolute z-50 w-full bg-input-bg border rounded-box shadow-xl max-h-96 overflow-hidden'

    // Search input
    const searchContainer = document.createElement('div')
    searchContainer.className = 'sticky top-0 p-2 bg-base-100 border-b'
    searchContainer.innerHTML = `
      <input type="text"
             data-search
             placeholder="Buscar..."
             class="input input-sm input-bordered w-full focus:outline-0 focus:border-base-content/50"
             autocomplete="off">
    `

    // Options list
    const optionsList = document.createElement('div')
    optionsList.dataset.optionsList = ''
    optionsList.className = 'overflow-y-auto max-h-64'

    // Populate options
    this.populateOptions(optionsList)

    menu.appendChild(searchContainer)
    menu.appendChild(optionsList)

    // Attach event listeners
    const searchInput = searchContainer.querySelector('[data-search]')
    searchInput.addEventListener('input', () => this.search())
    searchInput.addEventListener('keydown', (e) => this.handleKeydown(e))

    return menu
  }

  populateOptions(container) {
    const select = this.element
    const selectedValue = select.value
    const directOptions = Array.from(select.children).filter(child => {
      return child.tagName === 'OPTION' && (child.value !== '' || this.includeBlankValue)
    })

    directOptions.forEach(option => {
      const optionEl = this.createOptionElement(option, selectedValue, false)
      container.appendChild(optionEl)
    })

    // Check for optgroups
    const optgroups = select.querySelectorAll('optgroup')

    if (optgroups.length > 0) {
      // Grouped select
      optgroups.forEach(optgroup => {
        const groupDiv = document.createElement('div')
        groupDiv.dataset.optgroup = optgroup.label

        const groupHeader = document.createElement('div')
        groupHeader.className = 'px-3 py-2 bg-base-300 dark:bg-base-100 font-semibold text-sm text-base-content shadow-sm dark:shadow-md sticky top-0'

        if (this.hasOptgroupTemplate()) {
          groupHeader.appendChild(this.renderOptgroupTemplate(optgroup))
        } else {
          groupHeader.textContent = optgroup.label
        }

        groupDiv.appendChild(groupHeader)

        const groupOptions = optgroup.children || optgroup.querySelectorAll('option')
        Array.from(groupOptions).forEach(option => {
          if (option.value === '' && !this.includeBlankValue) return

          const optionEl = this.createOptionElement(option, selectedValue, true)
          groupDiv.appendChild(optionEl)
        })

        container.appendChild(groupDiv)
      })
    } else {
      // Direct options were already added above
    }
  }

  createOptionElement(option, selectedValue, indented) {
    const div = document.createElement('div')
    div.dataset.option = ''
    div.dataset.value = option.value
    div.dataset.text = option.text
    div.dataset.searchText = option.dataset.searchText || option.text
    div.setAttribute('role', 'option')
    div.className = this.optionClassName(indented)

    const content = document.createElement('div')
    content.className = 'min-w-0 flex-1'

    if (this.hasOptionTemplate()) {
      content.appendChild(this.renderOptionTemplate(option))
    } else {
      const textSpan = document.createElement('span')
      textSpan.className = 'truncate text-sm'
      textSpan.textContent = option.text
      content.appendChild(textSpan)
    }

    div.appendChild(content)

    const iconSpan = document.createElement('span')
    iconSpan.dataset.checkIcon = ''
    iconSpan.className = 'mr-3 shrink-0'
    iconSpan.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5">
        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
      </svg>
    `

    if (option.value === selectedValue) {
      div.classList.add('bg-primary', 'text-primary-content')
      div.setAttribute('aria-selected', 'true')
      iconSpan.classList.remove('hidden')
    } else {
      div.setAttribute('aria-selected', 'false')
      iconSpan.classList.add('hidden')
    }

    div.appendChild(iconSpan)

    div.onclick = () => this.selectOption(div)

    return div
  }

  optionClassName(indented) {
    const paddingClass = this.hasOptionTemplate()
      ? `${indented ? 'pl-6' : ''}`
      : `${indented ? 'pl-6 pr-3' : 'px-3'} py-2`

    return [
      paddingClass,
      'group',
      '[:not(.bg-primary)]:hover:bg-primary/20',
      'cursor-pointer',
      'flex',
      'items-center',
      'justify-between',
      'gap-2'
    ].filter(Boolean).join(' ')
  }

  hasOptionTemplate() {
    return this.optionTemplateElement instanceof HTMLTemplateElement
  }

  hasOptgroupTemplate() {
    return this.optgroupTemplateElement instanceof HTMLTemplateElement
  }

  renderOptionTemplate(option) {
    const fragment = this.optionTemplateElement.content.cloneNode(true)

    fragment.querySelectorAll('[data-field]').forEach((element) => {
      const value = option.dataset[element.dataset.field] || this.fallbackTemplateValue(option, element.dataset.field)

      if (value === undefined || value === null || value === '') {
        element.remove()
        return
      }

      element.textContent = value
    })

    return fragment
  }

  renderOptgroupTemplate(optgroup) {
    const fragment = this.optgroupTemplateElement.content.cloneNode(true)

    fragment.querySelectorAll('[data-field]').forEach((element) => {
      const value = optgroup.dataset[element.dataset.field] || this.fallbackOptgroupTemplateValue(optgroup, element.dataset.field)

      if (value === undefined || value === null || value === '') {
        element.remove()
        return
      }

      element.textContent = value
    })

    fragment.querySelectorAll('[data-html-field]').forEach((element) => {
      const value = optgroup.dataset[element.dataset.htmlField]

      if (value === undefined || value === null || value === '') {
        element.remove()
        return
      }

      element.innerHTML = value
    })

    return fragment
  }

  fallbackTemplateValue(option, field) {
    if (field === 'title') {
      return option.text
    }

    return null
  }

  fallbackOptgroupTemplateValue(optgroup, field) {
    if (field === 'title') {
      return optgroup.label
    }

    return null
  }

  toggle() {
    if (this.menuElement.classList.contains('hidden')) {
      this.open()
    } else {
      this.close()
    }
  }

  open() {
    this.menuElement.classList.remove('hidden')

    // Focus search input
    //setTimeout(() => this.searchElement.focus(), 50)
    this.searchElement.focus()

    // Add click outside listener
    this.boundHandleClickOutside = this.handleClickOutside.bind(this)
    setTimeout(() => {
      document.addEventListener('click', this.boundHandleClickOutside)
    }, 0)
  }

  close() {
    this.menuElement.classList.add('hidden')

    // Remove click outside listener
    document.removeEventListener('click', this.boundHandleClickOutside)

    // Clear search
    this.searchElement.value = ''
    this.showAllOptions()

    // Reset highlight
    this.selectedIndex = -1
    this.removeHighlights()
  }

  handleClickOutside(event) {
    if (!this.customWrapper.contains(event.target)) {
      this.close()
    }
  }

  selectOption(optionElement) {
    const value = optionElement.dataset.value
    const text = optionElement.dataset.text
    const hasValue = value !== ''

    // Update native select
    this.element.value = value
    this.element.dispatchEvent(new Event('change', { bubbles: true }))

    // Update button display
    const display = this.buttonElement.querySelector('[data-display]')
    if (display) {
      display.textContent = text
      display.classList.toggle('text-base-content/60', !hasValue)
    }

    // Update visual selection
    const allOptions = this.optionsListElement.querySelectorAll('[data-option]')
    allOptions.forEach(opt => {
      opt.classList.remove('bg-primary', 'text-primary-content')
      opt.setAttribute('aria-selected', 'false')
      const icon = opt.querySelector('[data-check-icon]')
      if (icon) icon.classList.add('hidden')
    })
    optionElement.classList.add('bg-primary', 'text-primary-content')
    optionElement.setAttribute('aria-selected', 'true')
    const selectedIcon = optionElement.querySelector('[data-check-icon]')
    if (selectedIcon) selectedIcon.classList.remove('hidden')

    this.close()
  }

  // Remove accents from a string for accent-insensitive search
  normalizeText(text) {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
  }

  search() {
    const searchTerm = this.normalizeText(this.searchElement.value.trim())

    if (searchTerm === '') {
      this.showAllOptions()
      return
    }

    this.filterOptions(searchTerm)
  }

  filterOptions(searchTerm) {
    let hasVisibleOptions = false
    const groups = this.optionsListElement.querySelectorAll('[data-optgroup]')

    if (groups.length > 0) {
      // Grouped select
      groups.forEach(group => {
        const options = group.querySelectorAll('[data-option]')
        let hasVisibleInGroup = false

        options.forEach(option => {
          const text = this.normalizeText(option.dataset.searchText || option.dataset.text)
          const matches = text.includes(searchTerm)

          option.classList.toggle('hidden', !matches)
          if (matches) {
            hasVisibleInGroup = true
            hasVisibleOptions = true
          }
        })

        group.classList.toggle('hidden', !hasVisibleInGroup)
      })
    } else {
      // Regular select
      const options = this.optionsListElement.querySelectorAll('[data-option]')
      options.forEach(option => {
        const text = this.normalizeText(option.dataset.searchText || option.dataset.text)
        const matches = text.includes(searchTerm)

        option.classList.toggle('hidden', !matches)
        if (matches) hasVisibleOptions = true
      })
    }

    this.showNoResultsMessage(!hasVisibleOptions)
  }

  showAllOptions() {
    const options = this.optionsListElement.querySelectorAll('[data-option]')
    options.forEach(opt => opt.classList.remove('hidden'))

    const groups = this.optionsListElement.querySelectorAll('[data-optgroup]')
    groups.forEach(group => group.classList.remove('hidden'))

    this.removeNoResultsMessage()
  }

  showNoResultsMessage(show) {
    this.removeNoResultsMessage()

    if (show) {
      const message = document.createElement('div')
      message.className = 'px-3 py-4 text-base-content/60 text-sm text-center'
      message.dataset.noResults = ''
      message.textContent = 'Nenhum resultado encontrado'
      this.optionsListElement.appendChild(message)
    }
  }

  removeNoResultsMessage() {
    const existing = this.optionsListElement.querySelector('[data-no-results]')
    if (existing) existing.remove()
  }

  handleKeydown(event) {
    const visibleOptions = Array.from(this.optionsListElement.querySelectorAll('[data-option]'))
      .filter(opt => !opt.classList.contains('hidden'))

    if (visibleOptions.length === 0) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, visibleOptions.length - 1)
        this.highlightOption(visibleOptions[this.selectedIndex])
        break

      case 'ArrowUp':
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
        this.highlightOption(visibleOptions[this.selectedIndex])
        break

      case 'Enter':
        event.preventDefault()
        if (this.selectedIndex >= 0) {
          visibleOptions[this.selectedIndex].click()
        }
        break

      case 'Escape':
        event.preventDefault()
        this.close()
        break
    }
  }

  highlightOption(option) {
    this.removeHighlights()

    if (option && !option.classList.contains('bg-primary')) {
      option.classList.add('bg-base-300')
      option.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }

  removeHighlights() {
    const options = this.optionsListElement.querySelectorAll('[data-option]')
    options.forEach(opt => opt.classList.remove('bg-base-300'))
  }
}
